import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  PieChart, Pie, Cell, ResponsiveContainer, Tooltip,
  BarChart, Bar, XAxis, YAxis, CartesianGrid,
  Treemap, RadialBarChart, RadialBar, Legend
} from 'recharts';
import Icon from '../../../components/AppIcon';
import { astraAPI } from '../../../services/astraApi';
import { SkeletonChart } from '../../../components/ui/Skeleton';

/**
 * Asset Allocation Component
 *
 * 🔧 PRODUCTION VERSION (2025-12-29): Enhanced Balance Overview & Insights
 *
 * Features:
 * 1. Balance Overview with Total / Invested / Available breakdown
 * 2. Separate INR and USDT available balance display
 * 3. Realized vs Unrealized P&L breakdown
 * 4. Utilization metrics per asset class
 * 5. Smart insights and recommendations
 * 6. Multiple chart visualization types
 */

const AssetAllocation = ({ refreshTrigger = 0 }) => {
  const [allocationData, setAllocationData] = useState([]);
  const [detailedAllocationData, setDetailedAllocationData] = useState([]);
  const [currencyData, setCurrencyData] = useState(null);
  const [loading, setLoading] = useState(true);
  // 🔧 2025-12-30 FIX: Prevent flickering on refresh - only show skeleton on INITIAL load
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const [error, setError] = useState(null);
  const [chartType, setChartType] = useState('donut');
  const [systemConfig, setSystemConfig] = useState(null);
  const [tradingMode, setTradingMode] = useState({ mode: 'paper' });
  const [showDetails, setShowDetails] = useState(false);

  useEffect(() => {
    fetchSystemConfigAndAllocation();
  }, [refreshTrigger]);

  const fetchSystemConfigAndAllocation = async () => {
    try {
      // Fetch system config first to know enabled asset classes
      const config = await astraAPI.getSystemConfig();
      const mode = await astraAPI.getTradingMode();

      setSystemConfig(config);
      setTradingMode(mode);

      // Now fetch allocation with config context
      await fetchAssetAllocation(config, mode);
    } catch (error) {
      console.error('Failed to fetch system config:', error);
      // Continue with default config
      await fetchAssetAllocation(null, { mode: 'paper' });
    }
  };

  const fetchAssetAllocation = async (config, mode) => {
    try {
      // 🔧 2025-12-30 FIX: Only show loading skeleton on INITIAL load
      if (isInitialLoad) {
        setLoading(true);
      }
      setError(null);

      // 🔧 PRODUCTION FIX (2025-10-29): Use currency breakdown for accurate calculations
      const currencyBreakdown = await astraAPI.getCurrencyBreakdown(1453);
      const positions = await astraAPI.getPortfolioAnalytics();

      // Store currency data for Balance Overview display
      setCurrencyData(currencyBreakdown);

      console.log('🔍 Asset Allocation Debug:', {
        positionsCount: positions?.items?.length || 0,
        currencyBreakdown: currencyBreakdown,
        enabledClasses: config?.enabled_names || ['Indian Equities', 'Crypto Spot']
      });

      if (positions && positions.items && currencyBreakdown) {
        // Get exchange rate from currency breakdown
        const exchangeRate = currencyBreakdown.combined.exchange_rate;

        // 🔧 PRODUCTION FIX: Only categorize ENABLED asset classes with proper currency conversion
        const enabledNames = config?.enabled_names || ['Indian Equities', 'Crypto Spot'];
        const categorizedAssets = categorizeAssets(positions.items, enabledNames, exchangeRate);

        // 🔧 CRITICAL FIX: Use CORRECT total value from currency breakdown
        // This accounts for both INR and USDT properly converted
        const totalValue = currencyBreakdown.combined.total_value_inr;

        // Calculate available balances from currency breakdown (all in INR)
        const inrAvailable = currencyBreakdown.inr.available;
        const usdtAvailableInINR = currencyBreakdown.usdt.available * exchangeRate;
        const totalAvailableBalance = inrAvailable + usdtAvailableInINR;

        // 🔧 PRODUCTION FIX: Mode-aware naming
        const balanceLabel = mode?.mode === 'paper'
          ? 'Available Balance (Simulated)'
          : 'Available Balance (Broker)';

        // Create enhanced allocation data with better naming
        const colors = {
          'Indian Equities': '#3B82F6',
          'Crypto Spot': '#10B981',
          'Available Balance (Simulated)': '#6B7280',
          'Available Balance (Broker)': '#10B981',
          // Disabled classes (won't be used):
          'Indian Options': '#8B5CF6',
          'Indian Futures': '#EF4444',
          'Indian Commodities': '#F59E0B',
          'Crypto Futures': '#06B6D4',
          'Other Assets': '#8B5CF6'
        };

        const newAllocationData = [];
        const newDetailedData = [];

        // Add categorized assets (only enabled ones)
        Object.entries(categorizedAssets).forEach(([category, data]) => {
          if (data.totalValue > 0 && enabledNames.includes(category)) {
            const percentage = ((data.totalValue / totalValue) * 100).toFixed(1);

            // 🔧 CRITICAL FIX (2025-10-30): Use total currency P&L for avgReturn, not just position P&L
            let avgReturn = data.avgReturn;
            if (category === 'Indian Equities') {
              // Use INR P&L from currency breakdown (includes cash gains)
              const inrTotal = currencyBreakdown.inr.total;
              const inrInitial = currencyBreakdown.inr.initial;
              avgReturn = ((inrTotal - inrInitial) / inrInitial * 100).toFixed(1);
            } else if (category === 'Crypto Spot') {
              // Use USDT P&L from currency breakdown (includes cash gains)
              const usdtTotal = currencyBreakdown.usdt.total;
              const usdtInitial = currencyBreakdown.usdt.initial;
              avgReturn = ((usdtTotal - usdtInitial) / usdtInitial * 100).toFixed(1);
            }

            newAllocationData.push({
              name: category,
              value: data.totalValue,
              percentage: parseFloat(percentage),
              color: colors[category] || colors['Other Assets'],
              count: data.positions.length,
              avgReturn: parseFloat(avgReturn)
            });

            // Add detailed breakdown
            data.positions.forEach(position => {
              newDetailedData.push({
                name: position.symbol,
                category: category,
                value: position.current_value,
                percentage: ((position.current_value / totalValue) * 100).toFixed(1),
                color: colors[category] || colors['Other Assets'],
                pnl: position.unrealized_pnl || 0,
                pnlPercentage: position.pnl_percentage || 0
              });
            });
          }
        });

        // 🔧 CRITICAL FIX (2025-10-29): Add available balance with correct percentage
        const balancePercentage = (totalAvailableBalance / totalValue * 100);
        if (totalAvailableBalance > 0) {
          newAllocationData.push({
            name: balanceLabel,
            value: totalAvailableBalance,
            percentage: parseFloat(balancePercentage.toFixed(1)),
            color: colors[balanceLabel],
            count: 1,
            avgReturn: 0
          });
        }

        setAllocationData(newAllocationData);
        setDetailedAllocationData(newDetailedData);
      } else {
        throw new Error('Failed to fetch asset allocation data');
      }
    } catch (err) {
      console.error('Failed to fetch asset allocation:', err);
      setError('Failed to load asset allocation');
      // 🔧 PRODUCTION FIX: NO FAKE FALLBACK - Set empty data
      setAllocationData([]);
    } finally {
      setLoading(false);
      // 🔧 FIX: Mark initial load complete - subsequent refreshes won't show skeleton
      setIsInitialLoad(false);
    }
  };

  // 🔧 PRODUCTION FIX: Enhanced asset categorization with proper currency conversion
  const categorizeAssets = (positions, enabledNames = ['Indian Equities', 'Crypto Spot'], exchangeRate = 88.33) => {
    // Build categories object dynamically based on enabled classes
    const categories = {};

    enabledNames.forEach(className => {
      categories[className] = { positions: [], totalValue: 0, totalPnl: 0 };
    });

    positions.forEach(position => {
      const symbol = position.symbol;
      const isCrypto = symbol.includes('USDT') || symbol.includes('BTC') || symbol.includes('ETH');

      // 🔧 CRITICAL FIX (2025-10-29): Convert crypto values from USDT to INR
      // Backend stores crypto values in USDT, frontend needs them in INR for consistent display
      const valueMultiplier = isCrypto ? exchangeRate : 1;
      const value = (position.current_value || 0) * valueMultiplier;
      const pnl = (position.unrealized_pnl || 0) * valueMultiplier;

      // Categorization logic - only for enabled classes
      if (symbol.includes('.NS') || symbol.includes('.BSE')) {
        // Indian stocks (already in INR)
        if (enabledNames.includes('Indian Equities')) {
          categories['Indian Equities'].positions.push({...position, current_value_inr: value, pnl_inr: pnl});
          categories['Indian Equities'].totalValue += value;
          categories['Indian Equities'].totalPnl += pnl;
        }
        // Skip Indian Options, Futures, Commodities if not enabled
      } else if (isCrypto) {
        // Crypto (convert USDT to INR)
        if (symbol.includes('USDTM') || symbol.includes('USDM')) {
          // Crypto Futures
          if (enabledNames.includes('Crypto Futures')) {
            categories['Crypto Futures'].positions.push({...position, current_value_inr: value, pnl_inr: pnl});
            categories['Crypto Futures'].totalValue += value;
            categories['Crypto Futures'].totalPnl += pnl;
          }
        } else {
          // Crypto Spot
          if (enabledNames.includes('Crypto Spot')) {
            categories['Crypto Spot'].positions.push({...position, current_value_inr: value, pnl_inr: pnl});
            categories['Crypto Spot'].totalValue += value;
            categories['Crypto Spot'].totalPnl += pnl;
          }
        }
      }
    });

    // Calculate average returns
    Object.keys(categories).forEach(category => {
      const data = categories[category];
      if (data.totalValue > 0) {
        data.avgReturn = ((data.totalPnl / data.totalValue) * 100).toFixed(1);
      } else {
        data.avgReturn = 0;
      }
    });

    return categories;
  };

  const formatCurrency = (amount, currency = 'INR') => {
    if (currency === 'USDT') {
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
        minimumFractionDigits: 2
      }).format(amount);
    }
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 0
    }).format(amount);
  };

  const formatCompactNumber = (amount) => {
    if (amount >= 10000000) return `₹${(amount / 10000000).toFixed(2)}Cr`;
    if (amount >= 100000) return `₹${(amount / 100000).toFixed(2)}L`;
    if (amount >= 1000) return `₹${(amount / 1000).toFixed(1)}K`;
    return `₹${amount.toFixed(0)}`;
  };

  const getInsights = () => {
    if (!currencyData || !allocationData.length) return [];

    const insights = [];
    const inr = currencyData.inr || {};
    const usdt = currencyData.usdt || {};
    const exchangeRate = currencyData.combined?.exchange_rate || 88;

    // Calculate total P&L with safety checks
    const totalPnl = (inr.pnl || 0) + ((usdt.pnl || 0) * exchangeRate);
    const totalInitial = (inr.initial || 0) + ((usdt.initial || 0) * exchangeRate);
    const totalPnlPercent = totalInitial > 0 ? ((totalPnl / totalInitial) * 100).toFixed(2) : '0.00';

    // Utilization insight with safety checks
    const inrUtilized = inr.utilized_percentage || 0;
    const usdtUtilized = usdt.utilized_percentage || 0;
    const avgUtilization = (inrUtilized + usdtUtilized) / 2;
    if (avgUtilization < 30) {
      insights.push({
        icon: 'AlertCircle',
        type: 'info',
        text: `Low capital utilization (${avgUtilization.toFixed(0)}%). Consider deploying more capital.`
      });
    } else if (avgUtilization > 80) {
      insights.push({
        icon: 'Shield',
        type: 'warning',
        text: `High utilization (${avgUtilization.toFixed(0)}%). Keep some cash for opportunities.`
      });
    }

    // P&L insight
    if (totalPnl > 0) {
      insights.push({
        icon: 'TrendingUp',
        type: 'success',
        text: `Portfolio up ${totalPnlPercent}% with ₹${formatCompactNumber(totalPnl).replace('₹', '')} profit.`
      });
    } else if (totalPnl < 0) {
      insights.push({
        icon: 'TrendingDown',
        type: 'error',
        text: `Portfolio down ${Math.abs(totalPnlPercent)}%. Consider rebalancing.`
      });
    }

    // Best performing asset class
    const invested = allocationData.filter(a => !a.name.includes('Available'));
    if (invested.length > 1) {
      const best = invested.reduce((a, b) => (a.avgReturn || 0) > (b.avgReturn || 0) ? a : b);
      if (best.avgReturn > 0) {
        insights.push({
          icon: 'Award',
          type: 'success',
          text: `${best.name} is your top performer at +${best.avgReturn}%`
        });
      }
    }

    return insights;
  };

  const CustomTooltip = ({ active, payload }) => {
    if (active && payload && payload?.length) {
      const data = payload?.[0]?.payload;
      return (
        <div className="bg-popover border border-border rounded-lg p-4 shadow-elevated min-w-[200px]">
          <div className="flex items-center space-x-2 mb-2">
            <div
              className="w-3 h-3 rounded-full"
              style={{ backgroundColor: data?.color }}
            ></div>
            <p className="font-semibold text-popover-foreground">{data?.name}</p>
          </div>
          <div className="space-y-1">
            <p className="text-sm text-muted-foreground">
              <span className="font-medium">Value:</span> {formatCurrency(data?.value)}
            </p>
            <p className="text-sm text-muted-foreground">
              <span className="font-medium">Allocation:</span> {data?.percentage}%
            </p>
            {data?.count && (
              <p className="text-sm text-muted-foreground">
                <span className="font-medium">Positions:</span> {data?.count}
              </p>
            )}
            {data?.avgReturn !== undefined && data?.avgReturn !== 0 && (
              <p className={`text-sm ${data?.avgReturn >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                <span className="font-medium">Avg Return:</span> {data?.avgReturn}%
              </p>
            )}
          </div>
        </div>
      );
    }
    return null;
  };

  const renderChart = () => {
    switch (chartType) {
      case 'donut':
        return (
          <ResponsiveContainer width="100%" height="100%">
            <PieChart>
              <Pie
                data={allocationData}
                cx="50%"
                cy="50%"
                innerRadius={60}
                outerRadius={100}
                paddingAngle={2}
                dataKey="value"
              >
                {allocationData?.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={entry?.color} />
                ))}
              </Pie>
              <Tooltip content={<CustomTooltip />} />
            </PieChart>
          </ResponsiveContainer>
        );

      case 'bar':
        return (
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={allocationData} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" />
              <XAxis
                dataKey="name"
                stroke="var(--color-muted-foreground)"
                fontSize={12}
                angle={-45}
                textAnchor="end"
                height={80}
              />
              <YAxis
                stroke="var(--color-muted-foreground)"
                fontSize={12}
                tickFormatter={(value) => `${(value / 1000000).toFixed(1)}M`}
              />
              <Tooltip content={<CustomTooltip />} />
              <Bar dataKey="value" radius={[4, 4, 0, 0]}>
                {allocationData?.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={entry?.color} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        );

      case 'treemap':
        return (
          <ResponsiveContainer width="100%" height="100%">
            <Treemap
              data={allocationData}
              dataKey="value"
              aspectRatio={4/3}
              stroke="#fff"
              fill="#8884d8"
            >
              <Tooltip content={<CustomTooltip />} />
            </Treemap>
          </ResponsiveContainer>
        );

      case 'radial':
        return (
          <ResponsiveContainer width="100%" height="100%">
            <RadialBarChart cx="50%" cy="50%" innerRadius="20%" outerRadius="90%" data={allocationData}>
              <RadialBar dataKey="percentage" cornerRadius={10} fill="#8884d8">
                {allocationData?.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={entry?.color} />
                ))}
              </RadialBar>
              <Tooltip content={<CustomTooltip />} />
              <Legend />
            </RadialBarChart>
          </ResponsiveContainer>
        );

      default:
        return null;
    }
  };

  // 🔧 2025-12-30 FIX: Only show skeleton on INITIAL load, not on refreshes
  if (loading && isInitialLoad) {
    return (
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        className="bg-card border border-border rounded-lg p-6"
      >
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-lg font-semibold text-foreground">Asset Allocation</h2>
          <div className="flex items-center space-x-2">
            <div className="w-2 h-2 bg-warning rounded-full animate-live-pulse"></div>
            <span className="text-xs text-muted-foreground">Loading...</span>
          </div>
        </div>
        <SkeletonChart type="pie" height={280} />
      </motion.div>
    );
  }

  if (error) {
    return (
      <div className="bg-card border border-border rounded-lg p-6">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-lg font-semibold text-foreground">Asset Allocation</h2>
          <div className="flex items-center space-x-2">
            <div className="w-2 h-2 bg-error rounded-full"></div>
            <span className="text-xs text-muted-foreground">Error</span>
          </div>
        </div>
        <div className="flex items-center justify-center py-12">
          <div className="text-center">
            <Icon name="AlertTriangle" size={24} className="text-error mx-auto mb-2" />
            <p className="text-sm text-muted-foreground mb-3">{error}</p>
            <button
              onClick={() => fetchSystemConfigAndAllocation()}
              className="px-4 py-2 text-sm bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors"
            >
              <div className="flex items-center space-x-2">
                <Icon name="RefreshCw" size={16} />
                <span>Retry</span>
              </div>
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (!allocationData || allocationData.length === 0) {
    return (
      <div className="bg-card border border-border rounded-lg p-6">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-lg font-semibold text-foreground">Asset Allocation</h2>
        </div>
        <div className="flex items-center justify-center py-12">
          <div className="text-center">
            <Icon name="PieChart" size={24} className="text-muted-foreground mx-auto mb-2" />
            <p className="text-sm text-muted-foreground">No positions to display</p>
          </div>
        </div>
      </div>
    );
  }

  const insights = getInsights();

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
      className="bg-card border border-border rounded-lg p-6 glass-card-hover"
    >
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="text-lg font-semibold text-foreground">Asset Allocation</h2>
          <p className="text-sm text-muted-foreground">Portfolio distribution & balance overview</p>
        </div>
        <div className="flex items-center space-x-3">
          <button
            onClick={() => setShowDetails(!showDetails)}
            className="text-xs text-primary hover:underline flex items-center space-x-1"
          >
            <Icon name={showDetails ? 'ChevronUp' : 'ChevronDown'} size={14} />
            <span>{showDetails ? 'Less' : 'Details'}</span>
          </button>
          <div className="flex items-center space-x-2">
            <div className="w-2 h-2 bg-success rounded-full animate-live-pulse"></div>
            <span className="text-xs text-muted-foreground">Live</span>
          </div>
        </div>
      </div>

      {/* Balance Overview Section - The Main Enhancement */}
      {currencyData && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="mb-6 p-4 bg-gradient-to-r from-primary/5 via-transparent to-success/5 rounded-xl border border-border"
        >
          {/* Total Portfolio Value */}
          <div className="text-center mb-4">
            <div className="text-xs text-muted-foreground uppercase tracking-wider mb-1">Total Portfolio Value</div>
            <div className="text-3xl font-bold text-foreground">
              {formatCurrency(currencyData.combined.total_value_inr)}
            </div>
            <div className={`text-sm font-medium ${(currencyData.combined.total_pnl_inr || 0) >= 0 ? 'text-success' : 'text-destructive'}`}>
              {(currencyData.combined.total_pnl_inr || 0) >= 0 ? '+' : ''}
              {formatCurrency(currencyData.combined.total_pnl_inr || 0)} (
              {(() => {
                const initial = (currencyData.inr?.initial || 0) + (currencyData.usdt?.initial || 0) * (currencyData.combined?.exchange_rate || 88);
                const pnl = currencyData.combined?.total_pnl_inr || 0;
                return initial > 0 ? ((pnl / initial) * 100).toFixed(2) : '0.00';
              })()}%)
            </div>
          </div>

          {/* Three Column Breakdown */}
          <div className="grid grid-cols-3 gap-4 text-center">
            {/* Initial Capital */}
            <div className="p-3 bg-muted/30 rounded-lg">
              <div className="text-xs text-muted-foreground mb-1">Initial Capital</div>
              <div className="text-lg font-semibold text-foreground">
                {formatCompactNumber(currencyData.inr.initial + currencyData.usdt.initial * currencyData.combined.exchange_rate)}
              </div>
            </div>

            {/* Currently Invested */}
            <div className="p-3 bg-primary/10 rounded-lg border border-primary/20">
              <div className="text-xs text-primary mb-1">Invested</div>
              <div className="text-lg font-semibold text-foreground">
                {formatCompactNumber(currencyData.inr.invested + currencyData.usdt.invested * currencyData.combined.exchange_rate)}
              </div>
              <div className="text-xs text-muted-foreground">
                {((currencyData.inr.invested + currencyData.usdt.invested * currencyData.combined.exchange_rate) /
                  currencyData.combined.total_value_inr * 100).toFixed(0)}% deployed
              </div>
            </div>

            {/* Available Balance */}
            <div className="p-3 bg-success/10 rounded-lg border border-success/20">
              <div className="text-xs text-success mb-1">Available</div>
              <div className="text-lg font-semibold text-foreground">
                {formatCompactNumber(currencyData.inr.available + currencyData.usdt.available * currencyData.combined.exchange_rate)}
              </div>
              <div className="text-xs text-muted-foreground">Ready to trade</div>
            </div>
          </div>

          {/* Detailed Currency Breakdown - Collapsible */}
          <AnimatePresence>
            {showDetails && (
              <motion.div
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: 'auto', opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                className="overflow-hidden"
              >
                <div className="mt-4 pt-4 border-t border-border grid grid-cols-2 gap-4">
                  {/* INR Details */}
                  <div className="p-3 bg-blue-500/5 rounded-lg border border-blue-500/20">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm font-medium text-foreground flex items-center">
                        <span className="mr-2">🇮🇳</span> INR Account
                      </span>
                      <span className="text-xs bg-blue-500/10 text-blue-600 px-2 py-0.5 rounded">
                        {currencyData.inr.utilized_percentage.toFixed(0)}% used
                      </span>
                    </div>
                    <div className="space-y-1 text-xs">
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Available:</span>
                        <span className="font-medium text-success">{formatCurrency(currencyData.inr.available)}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Invested:</span>
                        <span className="font-medium">{formatCurrency(currencyData.inr.invested)}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Current Value:</span>
                        <span className="font-medium">{formatCurrency(currencyData.inr.current_value)}</span>
                      </div>
                      <div className="flex justify-between pt-1 border-t border-border">
                        <span className="text-muted-foreground">Realized P&L:</span>
                        <span className={`font-medium ${currencyData.inr.realized_pnl >= 0 ? 'text-success' : 'text-destructive'}`}>
                          {currencyData.inr.realized_pnl >= 0 ? '+' : ''}{formatCurrency(currencyData.inr.realized_pnl)}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Unrealized P&L:</span>
                        <span className={`font-medium ${currencyData.inr.unrealized_pnl >= 0 ? 'text-success' : 'text-destructive'}`}>
                          {currencyData.inr.unrealized_pnl >= 0 ? '+' : ''}{formatCurrency(currencyData.inr.unrealized_pnl)}
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* USDT Details */}
                  <div className="p-3 bg-green-500/5 rounded-lg border border-green-500/20">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm font-medium text-foreground flex items-center">
                        <span className="mr-2">💰</span> USDT Account
                      </span>
                      <span className="text-xs bg-green-500/10 text-green-600 px-2 py-0.5 rounded">
                        {currencyData.usdt.utilized_percentage.toFixed(0)}% used
                      </span>
                    </div>
                    <div className="space-y-1 text-xs">
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Available:</span>
                        <span className="font-medium text-success">{formatCurrency(currencyData.usdt.available, 'USDT')}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Invested:</span>
                        <span className="font-medium">{formatCurrency(currencyData.usdt.invested, 'USDT')}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Current Value:</span>
                        <span className="font-medium">{formatCurrency(currencyData.usdt.current_value, 'USDT')}</span>
                      </div>
                      <div className="flex justify-between pt-1 border-t border-border">
                        <span className="text-muted-foreground">Realized P&L:</span>
                        <span className={`font-medium ${currencyData.usdt.realized_pnl >= 0 ? 'text-success' : 'text-destructive'}`}>
                          {currencyData.usdt.realized_pnl >= 0 ? '+' : ''}{formatCurrency(currencyData.usdt.realized_pnl, 'USDT')}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Unrealized P&L:</span>
                        <span className={`font-medium ${currencyData.usdt.unrealized_pnl >= 0 ? 'text-success' : 'text-destructive'}`}>
                          {currencyData.usdt.unrealized_pnl >= 0 ? '+' : ''}{formatCurrency(currencyData.usdt.unrealized_pnl, 'USDT')}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Exchange Rate */}
                <div className="mt-3 text-center text-xs text-muted-foreground">
                  Exchange Rate: 1 USD = ₹{currencyData.combined.exchange_rate.toFixed(2)}
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </motion.div>
      )}

      {/* Smart Insights */}
      {insights.length > 0 && (
        <div className="mb-6 space-y-2">
          {insights.map((insight, idx) => (
            <motion.div
              key={idx}
              initial={{ opacity: 0, x: -10 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: idx * 0.1 }}
              className={`flex items-center space-x-2 p-2 rounded-lg text-xs ${
                insight.type === 'success' ? 'bg-success/10 text-success' :
                insight.type === 'warning' ? 'bg-warning/10 text-warning' :
                insight.type === 'error' ? 'bg-destructive/10 text-destructive' :
                'bg-muted text-muted-foreground'
              }`}
            >
              <Icon name={insight.icon} size={14} />
              <span>{insight.text}</span>
            </motion.div>
          ))}
        </div>
      )}

      {/* Chart Type Switcher */}
      <div className="flex items-center justify-center mb-4">
        <div className="flex items-center space-x-1 bg-muted rounded-lg p-1">
          {[
            { key: 'donut', icon: 'PieChart', label: 'Donut' },
            { key: 'bar', icon: 'BarChart3', label: 'Bar' },
            { key: 'treemap', icon: 'LayoutGrid', label: 'Tree' },
            { key: 'radial', icon: 'Target', label: 'Radial' }
          ].map((type) => (
            <button
              key={type.key}
              onClick={() => setChartType(type.key)}
              className={`flex items-center space-x-1 px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${
                chartType === type.key
                  ? 'bg-primary text-primary-foreground shadow-sm'
                  : 'text-muted-foreground hover:text-foreground hover:bg-muted/80'
              }`}
            >
              <Icon name={type.icon} size={12} />
              <span>{type.label}</span>
            </button>
          ))}
        </div>
      </div>

      {/* Chart Display */}
      <div className="h-64 mb-4">
        {renderChart()}
      </div>

      {/* Enhanced Legend with Performance Metrics */}
      <div className="space-y-2">
        {allocationData?.map((item, index) => (
          <motion.div
            key={index}
            initial={{ opacity: 0, y: 5 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: index * 0.05 }}
            className="flex items-center justify-between p-3 bg-muted/30 rounded-lg hover:bg-muted/50 transition-colors group"
          >
            <div className="flex items-center space-x-3">
              <div
                className="w-3 h-3 rounded-full shadow-sm group-hover:scale-110 transition-transform"
                style={{ backgroundColor: item?.color }}
              ></div>
              <div>
                <span className="text-sm font-medium text-foreground">{item?.name}</span>
                {item?.count && item?.count > 0 && (
                  <span className="text-xs text-muted-foreground ml-2">
                    ({item?.count} {item?.count === 1 ? 'position' : 'positions'})
                  </span>
                )}
              </div>
            </div>
            <div className="text-right flex items-center space-x-3">
              <div>
                <div className="text-sm font-semibold text-foreground">
                  {formatCurrency(item?.value)}
                </div>
                <div className="text-xs text-muted-foreground">
                  {item?.percentage}% of total
                </div>
              </div>
              {item?.avgReturn !== undefined && item?.avgReturn !== 0 && (
                <div className={`px-2 py-1 rounded-lg text-xs font-medium ${
                  item?.avgReturn >= 0
                    ? 'bg-success/10 text-success'
                    : 'bg-destructive/10 text-destructive'
                }`}>
                  {item?.avgReturn >= 0 ? '+' : ''}{item?.avgReturn}%
                </div>
              )}
            </div>
          </motion.div>
        ))}
      </div>

      {/* Summary Stats - Compact */}
      <div className="mt-4 pt-4 border-t border-border">
        <div className="grid grid-cols-3 gap-4 text-center">
          <div>
            <div className="text-lg font-bold text-foreground">
              {allocationData?.length || 0}
            </div>
            <div className="text-xs text-muted-foreground">Asset Classes</div>
          </div>
          <div>
            <div className="text-lg font-bold text-foreground">
              {allocationData?.reduce((sum, item) => sum + (item?.count || 0), 0) || 0}
            </div>
            <div className="text-xs text-muted-foreground">Positions</div>
          </div>
          <div>
            <div className="text-lg font-bold text-foreground">
              {currencyData ? (
                ((currencyData.inr.utilized_percentage + currencyData.usdt.utilized_percentage) / 2).toFixed(0)
              ) : 0}%
            </div>
            <div className="text-xs text-muted-foreground">Utilized</div>
          </div>
        </div>
      </div>
    </motion.div>
  );
};

export default AssetAllocation;
